// TwoFactorSetup.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { setup2fa } from "../../../api/loginApi";
import { Form, Button, Alert, Card } from "react-bootstrap";
import useTranslation from "../../../hooks/useTranslation";
import "./TwoFactorSetup.scss";

const TwoFactorSetup = () => {
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation("settingsPage");
  const requestInProgress = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL_FOR_FRONTEND;
  useEffect(() => {
    // Prevent multiple requests
    if (requestInProgress.current) return;
    requestInProgress.current = true;

    // Fetch setup data
    const getSetupData = async () => {
      try {
        const data = await setup2fa();
        setSetupData(data);
      } catch (err) {
        setError(err.response?.data?.message || t("twofa_setup_error"));
      } finally {
        setLoading(false);
        // Don't reset requestInProgress.current here
      }
    };

    getSetupData();

    // Don't reset in cleanup either if you want to prevent all future calls
    // return () => {};
  }, [backendUrl, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      setError(t("enter_6digit_auth_app_code"));
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${backendUrl}/login/verify-setup-2fa`,
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || t("code_verify_failed"));
    }
  };

  if (loading) return <p>Loading...</p>;
  if (success) {
    return (
      <Alert variant="success">
        <Alert.Heading> {t("twofa_enabled")}</Alert.Heading>
        <p>{t("twofa_required_message")}</p>
      </Alert>
    );
  }

  return (
    <Card className="mb-4 twofa-setup-card">
      <Card.Header> {t("twofa_setup")}</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <h5>1. {t("install_app")}</h5>
        <p>{t("install_authenticator_app")}</p>

        <h5>2. {t("scan_qr_code")}</h5>
        <div className="text-center my-3">
          {setupData?.qrCodeUrl && (
            <img
              src={setupData.qrCodeUrl}
              alt="QR Code"
              style={{ maxWidth: "200px" }}
            />
          )}
        </div>

        <h5>3. {t("or_enter_code_manually")}</h5>
        <p className="text-monospace bg-light p-2">{setupData?.secret}</p>

        <h5>4. {t("enter_app_code")}</h5>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Control
              type="text"
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(
                  e.target.value.replace(/\D/g, "").substring(0, 6)
                )
              }
              placeholder={t("enter_6digit_auth_app_code")}
              maxLength={6}
              pattern="\d{6}"
              required
            />
            <Form.Text className="text-muted">
              {t("enter_6digit_auth_app_code")}
            </Form.Text>
          </Form.Group>
          <div className="twofa-setup-buttons-container">
            <Button type="submit" variant="primary">
              {t("verify_and_activate")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.location.reload()}
            >
              {t("cancel")}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TwoFactorSetup;
